import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getExam, getUsers, fetchQuestions, getGroups } from '../api';
import toast, { Toaster } from 'react-hot-toast';
import { 
  HomeIcon,
  DocumentTextIcon,
  UsersIcon,
  AcademicCapIcon,
  ChartBarIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  FolderIcon,
  ArrowTrendingUpIcon,
  CalendarDaysIcon,
  TrophyIcon
} from '@heroicons/react/24/outline';

const HomeDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalExams: 0,
    publishedExams: 0,
    draftExams: 0,
    totalStudents: 0,
    totalQuestions: 0,
    totalGroups: 0,
    upcomingExams: 0,
    completedExams: 0
  });
  const [recentExams, setRecentExams] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [exams, users, questions, groups] = await Promise.all([
        getExam(),
        getUsers(),
        fetchQuestions(),
        getGroups()
      ]);

      const publishedExams = exams.filter(exam => exam.estatus === '1');
      const draftExams = exams.filter(exam => exam.estatus === '0');
      
      // Get upcoming exams (future dates)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const upcoming = exams.filter(exam => {
        const examDate = new Date(exam.date);
        return examDate >= today;
      });

      setStats({
        totalExams: exams.length,
        publishedExams: publishedExams.length,
        draftExams: draftExams.length,
        totalStudents: users.length,
        totalQuestions: questions.length,
        totalGroups: groups.length,
        upcomingExams: upcoming.length,
        completedExams: exams.length - upcoming.length
      });

      // Get 5 most recent exams
      const sorted = [...exams].sort((a, b) => new Date(b.date) - new Date(a.date));
      setRecentExams(sorted.slice(0, 5));

      toast.success('Dashboard loaded successfully');
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex justify-center items-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-6">
      <Toaster position="top-right" />

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center">
              <HomeIcon className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Dashboard</h1>
              <p className="text-sm text-gray-500">Welcome back! Here's your system overview</p>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          {/* Total Exams Card */}
          <div 
            onClick={() => navigate('/admin/exam')}
            className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-lg p-6 text-white cursor-pointer transform hover:scale-105 transition-all"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <DocumentTextIcon className="h-7 w-7 text-white" />
              </div>
              <ArrowTrendingUpIcon className="h-6 w-6 text-white/70" />
            </div>
            <h3 className="text-3xl font-bold mb-1">{stats.totalExams}</h3>
            <p className="text-blue-100 text-sm font-medium">Total Exams</p>
            <div className="mt-4 pt-4 border-t border-white/20 flex justify-between text-xs">
              <span className="flex items-center">
                <CheckCircleIcon className="h-4 w-4 mr-1" />
                {stats.publishedExams} Published
              </span>
              <span className="flex items-center">
                <XCircleIcon className="h-4 w-4 mr-1" />
                {stats.draftExams} Draft
              </span>
            </div>
          </div>

          {/* Total Students Card */}
          <div 
            onClick={() => navigate('/admin/users')}
            className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl shadow-lg p-6 text-white cursor-pointer transform hover:scale-105 transition-all"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <UsersIcon className="h-7 w-7 text-white" />
              </div>
              <ChartBarIcon className="h-6 w-6 text-white/70" />
            </div>
            <h3 className="text-3xl font-bold mb-1">{stats.totalStudents}</h3>
            <p className="text-green-100 text-sm font-medium">Total Students</p>
            <div className="mt-4 pt-4 border-t border-white/20">
              <p className="text-xs text-green-100">Registered users in the system</p>
            </div>
          </div>

          {/* Total Questions Card */}
          <div 
            onClick={() => navigate('/admin/que')}
            className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl shadow-lg p-6 text-white cursor-pointer transform hover:scale-105 transition-all"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <AcademicCapIcon className="h-7 w-7 text-white" />
              </div>
              <FolderIcon className="h-6 w-6 text-white/70" />
            </div>
            <h3 className="text-3xl font-bold mb-1">{stats.totalQuestions}</h3>
            <p className="text-purple-100 text-sm font-medium">Total Questions</p>
            <div className="mt-4 pt-4 border-t border-white/20">
              <p className="text-xs text-purple-100">{stats.totalGroups} question groups</p>
            </div>
          </div>

          {/* Upcoming Exams Card */}
          <div 
            onClick={() => navigate('/admin/exam')}
            className="bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl shadow-lg p-6 text-white cursor-pointer transform hover:scale-105 transition-all"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <CalendarDaysIcon className="h-7 w-7 text-white" />
              </div>
              <ClockIcon className="h-6 w-6 text-white/70" />
            </div>
            <h3 className="text-3xl font-bold mb-1">{stats.upcomingExams}</h3>
            <p className="text-orange-100 text-sm font-medium">Upcoming Exams</p>
            <div className="mt-4 pt-4 border-t border-white/20">
              <p className="text-xs text-orange-100">{stats.completedExams} completed</p>
            </div>
          </div>
        </div>

        {/* Secondary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-semibold mb-1">Published Exams</p>
                <h4 className="text-2xl font-bold text-gray-800">{stats.publishedExams}</h4>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <CheckCircleIcon className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <div className="mt-4">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-green-600 h-2 rounded-full" 
                  style={{ width: `${stats.totalExams ? (stats.publishedExams / stats.totalExams) * 100 : 0}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {stats.totalExams ? Math.round((stats.publishedExams / stats.totalExams) * 100) : 0}% of total exams
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-semibold mb-1">Draft Exams</p>
                <h4 className="text-2xl font-bold text-gray-800">{stats.draftExams}</h4>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                <XCircleIcon className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
            <div className="mt-4">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-yellow-600 h-2 rounded-full" 
                  style={{ width: `${stats.totalExams ? (stats.draftExams / stats.totalExams) * 100 : 0}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {stats.totalExams ? Math.round((stats.draftExams / stats.totalExams) * 100) : 0}% of total exams
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-semibold mb-1">Question Groups</p>
                <h4 className="text-2xl font-bold text-gray-800">{stats.totalGroups}</h4>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <FolderIcon className="h-6 w-6 text-purple-600" />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-xs text-gray-500">
                Avg {stats.totalGroups ? Math.round(stats.totalQuestions / stats.totalGroups) : 0} questions per group
              </p>
            </div>
          </div>
        </div>

        {/* Recent Exams Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-800 flex items-center">
              <DocumentTextIcon className="h-6 w-6 mr-2 text-blue-600" />
              Recent Exams
            </h2>
            <button
              onClick={() => navigate('/admin/exam')}
              className="text-sm text-blue-600 font-semibold hover:text-blue-700 transition-colors"
            >
              View All →
            </button>
          </div>

          {recentExams.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 font-medium">No exams found</p>
              <button
                onClick={() => navigate('/admin/exam/add')}
                className="mt-4 inline-flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-2.5 rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all"
              >
                <DocumentTextIcon className="h-5 w-5" />
                <span>Create First Exam</span>
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Exam Name</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Class</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Date</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Duration</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Marks</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recentExams.map((exam) => (
                    <tr 
                      key={exam.eid} 
                      className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/admin/exam/view/${exam.eid}`)}
                    >
                      <td className="py-4 px-4">
                        <p className="font-semibold text-gray-800">{exam.name}</p>
                      </td>
                      <td className="py-4 px-4">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-purple-100 text-purple-800">
                          Class {exam.class}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center text-sm text-gray-600">
                          <CalendarDaysIcon className="h-4 w-4 mr-1" />
                          {exam.date}
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center text-sm text-gray-600">
                          <ClockIcon className="h-4 w-4 mr-1" />
                          {exam.duration} min
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center text-sm text-gray-600">
                          <TrophyIcon className="h-4 w-4 mr-1" />
                          {exam.tmarks}
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${
                          exam.estatus === '1' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {exam.estatus === '1' ? '✅ Published' : '📝 Draft'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
          <button
            onClick={() => navigate('/admin/exam/add')}
            className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-all flex items-center space-x-3"
          >
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <DocumentTextIcon className="h-5 w-5 text-blue-600" />
            </div>
            <span className="font-semibold text-gray-800">Create Exam</span>
          </button>

          <button
            onClick={() => navigate('/admin/que/add')}
            className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-all flex items-center space-x-3"
          >
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <AcademicCapIcon className="h-5 w-5 text-purple-600" />
            </div>
            <span className="font-semibold text-gray-800">Add Question</span>
          </button>

          <button
            onClick={() => navigate('/admin/users')}
            className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-all flex items-center space-x-3"
          >
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <UsersIcon className="h-5 w-5 text-green-600" />
            </div>
            <span className="font-semibold text-gray-800">View Students</span>
          </button>

          <button
            onClick={() => navigate('/admin/result')}
            className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-all flex items-center space-x-3"
          >
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <ChartBarIcon className="h-5 w-5 text-orange-600" />
            </div>
            <span className="font-semibold text-gray-800">View Results</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default HomeDashboard;
